import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform = 'chromium' | 'ios' | 'mac-safari' | 'android-other' | 'unsupported';

export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>('unsupported');
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('pwa-banner-dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setIsInstalled(isStandalone);

    // Detect platform for install instructions
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome|CriOS|FxiOS/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setPlatform('ios');
    } else if (isSafari && /Macintosh/.test(navigator.userAgent)) {
      setPlatform('mac-safari');
    } else if (isAndroid) {
      // Samsung Internet, Huawei, Firefox, Opera etc. — all support Add to Home Screen
      // via browser menu. beforeinstallprompt will upgrade to 'chromium' if it fires.
      setPlatform('android-other');
    }
    // Chromium-based browsers fire beforeinstallprompt (handled below, overrides android-other)

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      setPlatform('chromium');
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!installPrompt) return false;

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
        setInstallPrompt(null);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const dismissBanner = () => {
    setDismissed(true);
    try { sessionStorage.setItem('pwa-banner-dismissed', '1'); } catch { /* noop */ }
  };

  // Show install option on any platform that supports it — not just mobile
  const canInstall = !isInstalled && platform !== 'unsupported';
  const showBanner = canInstall && !dismissed;

  return {
    isInstallable,   // true when beforeinstallprompt has fired (can auto-prompt)
    isInstalled,
    isMobile,
    platform,        // 'chromium' | 'ios' | 'unsupported'
    promptInstall,
    dismissBanner,
    showInstallOption: canInstall,   // any supported platform, not just mobile
    showBanner,      // for dismissable CTA on Explore screen
  };
}
